import { Info, Volume2, VolumeX } from "lucide-react";
import { CancelIcon, ICON_SIZE } from "./actionIcons";
import { useState, useEffect } from "react";
import MockContextMenu from './MockContextMenu';

/**
 * `isLastStep` and `size` ARE PASSED BY JOYRIDE and were simply not read here,
 * which quietly killed two props every one of the eighteen render sites sets:
 *
 *   - `showProgress` put "3/14" into primaryProps.children, and
 *   - `locale={{ last: 'Finish' }}` put "Finish" there on the final step.
 *
 * Spreading {...primaryProps} and then writing `Next` as a JSX child overrides
 * that child, so the button read "Next" on every step of every tour including
 * the last, and no tour has ever shown its progress. Read them instead of
 * spreading and hoping.
 */
export default function CustomTooltip({ continuous, index, size, isLastStep, step, backProps, closeProps, primaryProps, skipProps, tooltipProps }) {
    const showNextHint = step.disableNext;
    const showPrevHint = step.disablePrev;

    //Joyride's own tooltip renders Back only when `index > 0` (Tooltip/Container
    //.tsx). This one gated on `backProps &&`, which Joyride always supplies — so
    //every tour showed a live Back button on its first step. In the record modal
    //that was destructive rather than merely odd: goTo(-1) finds no step and
    //ends the tour, so Back on step one closed it.
    const showBack = backProps && index > 0;

    //"Next" until the end, then whatever the site called the last button. Falls
    //back to Finish rather than Next for a site that sets no locale, because
    //a final step offering "Next" is a dead end that looks like a bug.
    const primaryLabel = isLastStep
        ? (step.locale?.last ?? 'Finish')
        : 'Next';
    
    // Check if this step should show the mock context menu
    const showMockMenu = step.showMockContextMenu;
    const mockMenuProps = step.mockContextMenuProps || {};
    
    // Text-to-speech state
    const [isSpeaking, setIsSpeaking] = useState(false);

    const handleClose = (e) => {
        e.stopPropagation();
        // Stop any ongoing speech when closing
        if (isSpeaking) {
            window.speechSynthesis.cancel();
            setIsSpeaking(false);
        }
        if (skipProps && skipProps.onClick) {
            skipProps.onClick(e);
        } else if (closeProps && closeProps.onClick) {
            closeProps.onClick(e);
        }
    };

    const handleTextToSpeech = (e) => {
        e.stopPropagation();
        
        if (isSpeaking) {
            // Stop speaking
            window.speechSynthesis.cancel();
            setIsSpeaking(false);
        } else {
            // Start speaking
            const textToRead = `${step.title || ''}. ${step.content || ''}`;
            const utterance = new SpeechSynthesisUtterance(textToRead);
            
            // Configure speech
            utterance.rate = 0.9; // Slightly slower for clarity
            utterance.pitch = 1;
            utterance.volume = 1;
            
            // Handle end of speech
            utterance.onend = () => {
                setIsSpeaking(false);
            };
            
            utterance.onerror = () => {
                setIsSpeaking(false);
            };
            
            window.speechSynthesis.speak(utterance);
            setIsSpeaking(true);
        }
    };

    // Stop speech when the step changes or the tooltip goes away.
    //
    // THE GUARD WAS THE BUG. This cleanup used to read `if (isSpeaking)`, but it
    // only re-registers when `index` changes, so the closure it runs is the one
    // built on the render where the step last changed — when isSpeaking was
    // false. Pressing the speaker and then Next therefore cancelled nothing: the
    // previous step's text kept reading over the new one. Closing the tour with
    // Esc or the backdrop skipped handleClose too, leaving speech running with
    // no tooltip left to stop it.
    //
    // Cancelling unconditionally needs no closure and is safe — cancel() on an
    // idle synth is a no-op — so there is nothing left to go stale. The reset of
    // `isSpeaking` has to happen here as well, or the button stays on VolumeX
    // ("Stop reading") for a step that is not being read.
    useEffect(() => {
        return () => {
            window.speechSynthesis?.cancel();
            setIsSpeaking(false);
        };
    }, [index]);

    //`data-tour-target` below is published for the same reason
    //`data-joyride-tooltip` is: nothing else in the DOM says which element a
    //running step is about. Joyride keeps the step in its own store and writes
    //nothing onto the target, so scripts/probe-tour-placement.mjs could not
    //check the SPOTLIGHT against the thing it is supposed to be a hole over —
    //the only rect it could reach was the spotlight's own, so a spotlight
    //sitting 150px off its control measured as perfectly clean. With this it
    //can check, on every tour that renders this tooltip.
    return (
        <div 
            {...tooltipProps} 
            data-joyride-tooltip="true"
            data-tour-target={typeof step.target === 'string' ? step.target : undefined}
            className="p-6 rounded-3xl shadow-xl bg-white max-w-sm border border-gray-100 
                       backdrop-blur-sm relative overflow-hidden pointer-events-auto"
            onClick={(e) => e.stopPropagation()}
        >
            {/* Gradient accent */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-[var(--theme-accent)]"></div>
            
            <div className="flex items-start justify-between gap-2 mb-3">
                {step.title && (
                    <h4 className="text-xl font-bold text-gray-900 leading-tight flex-1">
                        {step.title}
                    </h4>
                )}
                
                {/* Text-to-Speech Button */}
                {(step.title || step.content) && (
                    <button
                        onClick={handleTextToSpeech}
                        className={`flex-shrink-0 p-2 rounded-lg transition-all duration-200 ${
                            isSpeaking 
                                ? 'bg-[var(--theme-accent-tint)] text-[var(--theme-accent-ink)] hover:bg-[var(--theme-accent-tint)]' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        title={isSpeaking ? "Stop reading" : "Read aloud"}
                    >
                        {isSpeaking ? (
                            <VolumeX className="w-5 h-5" />
                        ) : (
                            <Volume2 className="w-5 h-5" />
                        )}
                    </button>
                )}
            </div>
            
            {step.content && (
                <div className="text-gray-600 mb-4 text-sm leading-relaxed">
                    {step.content}
                </div>
            )}

            {/* Show mock context menu if specified */}
            {showMockMenu && (
                <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                    <MockContextMenu {...mockMenuProps} />
                </div>
            )}

            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    {showBack && (
                        <button
                            {...backProps}
                            onClick={(e) => {
                                e.stopPropagation();
                                backProps.onClick(e);
                            }}
                            disabled={step.disablePrev}
                            className="btn-t btn-t-secondary"
                        >
                            Back
                        </button>
                    )}
                    {continuous && (
                        <button
                            {...primaryProps}
                            onClick={(e) => {
                                e.stopPropagation();
                                primaryProps.onClick(e);
                            }}
                            disabled={step.disableNext}
                            className="btn-t btn-t-primary shadow-md"
                        >
                            {primaryLabel}
                        </button>
                    )}
                    {/* Where you are, which every render site has been asking
                        for with `showProgress` and never got. Rendered as its
                        own element rather than inside the button label: a
                        primary button whose text changes width on every step
                        makes the footer jump. */}
                    {size > 1 && (
                        <span className="hint-t">
                            <span className="sr-only">Step {index + 1} of {size}</span>
                            <span aria-hidden="true">{index + 1}/{size}</span>
                        </span>
                    )}
                </div>

                <button 
                    onClick={handleClose}
                    className="btn-t btn-t-ghost"
                ><CancelIcon size={ICON_SIZE} />
                    Close
                </button>
            </div>

            {(showNextHint || showPrevHint) && (
                <div className="mt-2 p-2 bg-[var(--theme-accent-tint)] rounded-2xl border border-[var(--theme-accent-border)]">
                    <div className="flex items-center gap-2 text-xs text-[var(--theme-accent-ink)]">
                        <Info className="w-4 h-4 flex-shrink-0" />
                        <div>
                            {/* A step names what it wants clicked, and all
                                twenty-four gate steps in the app now do. The
                                fallback no longer says "tab": for most of the
                                run only five of them carried a hint, so the
                                other nineteen told the reader to click a tab
                                while the thing to click was a sidebar row or a
                                button — "Generate Report Analysis", "Preview
                                Messages", "Back to Edit". A gate whose
                                instruction names the wrong kind of control
                                reads as a broken tour, because the greyed-out
                                Next is the only other signal. */}
                            {showNextHint && <span>{step.disableNextHint || 'Complete the highlighted step to continue.'}</span>}
                            {showPrevHint && <span>{step.disablePrevHint || 'Complete the highlighted step to go back.'}</span>}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}